import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Map, MapPointProps } from "../framework/map/map";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    getWorkTimeTypeColorClass,
    getWorkTimeTypeColorCode,
    getWorkTimeTypeContrastColorClass,
} from "../../models/workShitTimeSlot/workTimeType";
import { SectionHeader } from "../layout/sectionHeader";
import { useTranslation } from "react-i18next";
import MuiMenu from "../framework/muiMenu";
import { MuiSwitch } from "../framework/muiSwitch";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { setGroupMapPoints } from "../../store/monitorViewSlice";
import { useWorkShiftTimeSlotMapData } from "../../hooks/workTime/useWorkShiftTimeSlotMapData";
import { useVehicleRouteMapData } from "../../hooks/monitorView/useVehicleRouteMapData";
import { useReload } from "../../hooks";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { VehicleData } from "../../models/monitorView/monitorViewTypes";
import { TabsEnum } from "./monitorViewUtils";

interface MonitorViewMapProps {
    loading: boolean;
    data: VehicleData[];
    onRefresh: () => void;
    selectedVehicleId: string;
    reloadKey: number;
    hoveredWorkShiftTimeSlotId: string;
    setSelectedVehicle: (id: string) => void;
    setSelectedEmployee: (id: string) => void;
    setSelectedWorkShiftTimeSlot: (id: string) => void;
}

export const MonitorViewMap = ({
    loading,
    data,
    onRefresh,
    selectedVehicleId,
    reloadKey,
    hoveredWorkShiftTimeSlotId,
    setSelectedVehicle,
    setSelectedEmployee,
    setSelectedWorkShiftTimeSlot,
}: MonitorViewMapProps) => {
    const [workShiftPositionsLoading, setWorkShiftPositionsLoading] =
        useState(false);
    const { t } = useTranslation();
    const groupPoints = useAppSelector(
        (state) => state.monitorView.groupMapPoints
    );
    const showNoWorkTime = useAppSelector(
        (state) => state.monitorViewFilters.noWorkTime
    );
    const selectedWorkTimeTypeIds = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypeIds
    );
    const selectedWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.selectedWorkShiftTimeSlotId
    );
    const workShift = useAppSelector(
        (state) => state.monitorView.selectedWorkShift
    );
    const isWorkShiftView = useAppSelector(
        (state) => state.monitorView.isWorkShiftView
    );
    const isVehicleRouteView = useAppSelector(
        (state) => state.monitorView.isVehicleRouteView
    );
    const vehicleRoute = useAppSelector((state) => state.monitorView.vehicleRoute);

    const dispatch = useAppDispatch();
    const [zoomKey, zoom] = useReload();
    const shouldZoom = useRef(false);

    const { routes, points, reload, loading: mapDataLoading } = useWorkShiftTimeSlotMapData(
        workShift?.workShiftTimeSlots ?? [],
        selectedWorkShiftTimeSlotId,
        hoveredWorkShiftTimeSlotId
    );

    const { routes: vehicleRoutes, points: vehiclePoints } = useVehicleRouteMapData(vehicleRoute);

    const tab = useAppSelector((state) => state.monitorView.activeTab);

    useEffect(() => {
        reload();
    }, [reloadKey]);

    useEffect(() => {
        if (!!workShift?.workShiftId) {
            setWorkShiftPositionsLoading(true);
            shouldZoom.current = true;
        }
    }, [workShift?.workShiftId]);

    useEffect(() => {
        // Zoom when workShift has changed and routes and points have been loaded
        if (shouldZoom.current && routes?.length > 0 && points?.length > 0) {
            setWorkShiftPositionsLoading(false);
            zoom();
            shouldZoom.current = false;
        }
    }, [routes, points]);

    useEffect(() => {
        // Zoom when filters have changed
        if (!!selectedWorkTimeTypeIds) {
            zoom();
        }
    }, [selectedWorkTimeTypeIds, showNoWorkTime]);

    useEffect(() => {
        setWorkShiftPositionsLoading(mapDataLoading);
    }, [mapDataLoading]);

    useEffect(() => {
        // Zoom when vehicleRoute changes
        if (vehicleRoute?.length > 0) {
            zoom();
        }
    }, [vehicleRoute]);

    const mapPoints = useMemo(
        () =>
            data
                ?.filter(
                    (d) =>
                        // Filter by selected vehicle, if present
                        !selectedVehicleId || selectedVehicleId === d.vehicleId
                )
                .map(
                    (d) =>
                        ({
                            id: d.vehicleId,
                            coords: d.coords,
                            icon: (
                                <LocalShippingIcon
                                    className={`text-${getWorkTimeTypeContrastColorClass(
                                        d.workTimeTypeType
                                    )}`}
                                />
                            ),
                            text: d.registerNumber,
                            fullText: [
                                d.registerNumber,
                                d.employeeName,
                                d.workTimeTypeName,
                            ]
                                .filter(Boolean)
                                .join(" - "),
                            textColorClass: getWorkTimeTypeContrastColorClass(
                                d.workTimeTypeType
                            ),
                            textBgColorClass: getWorkTimeTypeColorClass(
                                d.workTimeTypeType
                            ),
                            groupColor: getWorkTimeTypeColorCode(
                                d.workTimeTypeType
                            ),
                            groupName: d.workTimeTypeName,
                        } as MapPointProps)
                ),
        [data, selectedVehicleId]
    );

    return (
        <Box display="flex" flexDirection="column" minHeight="100%">
            <LoadingIndicator loading={workShiftPositionsLoading} />
            <div>
                <SectionHeader>
                    <Typography
                        component="div"
                        sx={{ flexGrow: 1 }}
                        className="section-header text-truncate"
                    >
                        {t("monitor")}
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={() => zoom()}
                        title={t("map.centerFocus")}
                    >
                        <CenterFocusStrongIcon />
                    </IconButton>
                    <IconButton
                        color="inherit"
                        onClick={onRefresh}
                        disabled={loading}
                    >
                        <RefreshIcon />
                    </IconButton>
                    <MuiMenu
                        label={<SettingsIcon color="white" />}
                        keepOpenAfterSelect
                        items={[
                            {
                                label: (
                                    <MuiSwitch
                                        label={t("map.groupIcons")}
                                        checked={groupPoints}
                                        readOnly
                                        onChange={(_, checked) =>
                                            dispatch(setGroupMapPoints(checked))
                                        }
                                    />
                                ),
                            },
                        ]}
                    />
                </SectionHeader>
            </div>
            {!!mapPoints ? (
                <Map
                    points={isVehicleRouteView ? vehiclePoints : !!workShift ? points : mapPoints}
                    routes={isVehicleRouteView ? vehicleRoutes : !!workShift ? routes : []}
                    autoZoom={false}
                    zoomKey={zoomKey}
                    groupPoints={groupPoints}
                    pointSelectionEnabled
                    onPointSelect={(id) => {
                        if (tab === TabsEnum.EMPLOYEES && isWorkShiftView) {
                            setSelectedWorkShiftTimeSlot(id);
                        } else if(tab === TabsEnum.EMPLOYEES) {
                            setSelectedEmployee(id);
                        }
                        if(tab === TabsEnum.VEHICLES) {
                            setSelectedVehicle(id);
                        }
                    }}
                    hoverMenuEnabled
                />
            ) : null}
        </Box>
    );
};